import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bike,
  CheckCircle2,
  ClipboardList,
  Clock,
  Download,
  MapPin,
  PackageX,
  RefreshCw,
  Truck,
  XCircle,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import StatCard from '../../components/StatCard';
import AssignRiderDrawer from '../../components/AssignRiderDrawer';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { useProducts } from '../../context/ProductsContext';
import { listRiders } from '../../api/orders';
import {
  DROP_ERRORS,
  DROP_STATUS,
  assignDrop,
  dateInIst,
  listDrops,
} from '../../api/subscriptions';
import { downloadRunSheetExcel } from '../../utils/runSheetExcel';
import { statusColors } from '../../theme';
import cardStyles from '../OrderQueue/OrderCard.module.css';
import boardStyles from '../OrderQueue/OrderQueue.module.css';
import styles from './Subscriptions.module.css';

const VIEW_OPS = 'ops';
const VIEW_PRODUCER = 'producer';

function defaultViewForDate(selectedDate) {
  return selectedDate === dateInIst(1) ? VIEW_PRODUCER : VIEW_OPS;
}

const RUN_SHEET_COLUMNS = [
  { key: DROP_STATUS.PENDING, title: 'Pending', hint: 'Ready to assign a rider' },
  { key: DROP_STATUS.OUT, title: 'Out', hint: 'Driver is on the way' },
  { key: DROP_STATUS.DELIVERED, title: 'Delivered', hint: 'Completed this morning' },
  { key: DROP_STATUS.FAILED, title: 'Failed', hint: 'Needs attention' },
  { key: DROP_STATUS.SKIPPED, title: 'Skipped', hint: 'Customer skipped this date' },
];

const KIND_EMOJI = {
  milk: '🥛',
  water: '💧',
  curd: '🫙',
};

function friendlyError(err) {
  return DROP_ERRORS[err.code || err.message] || err.message || 'Something went wrong.';
}

function DropCard({ drop, busy, onAssign }) {
  const accent = statusColors[drop.status]?.fg || '#B8850A';
  const canAssign = drop.status === DROP_STATUS.PENDING || drop.status === DROP_STATUS.OUT;
  const orderLabel = drop.orderId || drop.id;
  const emoji = KIND_EMOJI[drop.kind] || '🧺';

  return (
    <div
      className={`${cardStyles.card} ${drop.status === DROP_STATUS.OUT ? cardStyles.cardOutForDelivery : ''}`}
      style={{ '--accent': accent }}
    >
      <div className={cardStyles.top}>
        <span className={cardStyles.topLeft}>
          <span className={cardStyles.id}>{orderLabel}</span>
        </span>
        <span className={cardStyles.time}>{drop.kind || 'subs'}</span>
      </div>

      <p className={cardStyles.scheduleTag}>
        <Clock size={11} /> {drop.slotLabel || 'Morning'}
      </p>

      {drop.customerPhone && (
        <p className={cardStyles.meta}>{drop.customerPhone}</p>
      )}
      {drop.address && (
        <p className={cardStyles.meta}>
          <MapPin size={11} /> {drop.address.split(',')[0]}
        </p>
      )}

      <div className={cardStyles.items}>
        <span className={cardStyles.itemChip} title={drop.itemName || 'Item'}>{emoji}</span>
        <span className={cardStyles.itemCount}>
          {drop.brand ? `${drop.brand} · ` : ''}
          {drop.itemName || 'Subscription item'}
          {' · '}
          ×{drop.qty}
        </span>
      </div>

      <div className={cardStyles.bottom}>
        <span className={cardStyles.total}>Subscription</span>
        <span className={cardStyles.pay}>SUBS</span>
      </div>

      {(drop.riderName || drop.deliveryPersonSub) && (
        <div className={cardStyles.riderRow}>
          <Bike size={12} /> {drop.riderName || 'Rider assigned'}
        </div>
      )}

      {drop.stockShort && (
        <p className={cardStyles.failReason}>
          <PackageX size={12} style={{ verticalAlign: 'middle' }} /> Stock short
        </p>
      )}

      {canAssign && (
        <div className={cardStyles.actions}>
          <Button size="sm" variant="primary" icon={Bike} disabled={busy} onClick={() => onAssign(drop)}>
            {drop.deliveryPersonSub ? 'Reassign' : 'Assign rider'}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Producer packing table — sorted by kind / item for handoff. */
function ProducerTable({ date, drops, onDownload, downloading }) {
  const [brandFilter, setBrandFilter] = useState('ALL');

  const brandOptions = useMemo(() => {
    const set = new Set();
    drops.forEach((d) => {
      const brand = String(d.brand || '').trim();
      if (brand) set.add(brand);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [drops]);

  const filtered = useMemo(() => {
    if (brandFilter === 'ALL') return drops;
    if (brandFilter === '__NONE__') {
      return drops.filter((d) => !String(d.brand || '').trim());
    }
    return drops.filter((d) => String(d.brand || '').trim() === brandFilter);
  }, [drops, brandFilter]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const brandCmp = String(a.brand || '').localeCompare(String(b.brand || ''));
    if (brandCmp !== 0) return brandCmp;
    const kindCmp = String(a.kind || '').localeCompare(String(b.kind || ''));
    if (kindCmp !== 0) return kindCmp;
    const itemCmp = String(a.itemName || '').localeCompare(String(b.itemName || ''));
    if (itemCmp !== 0) return itemCmp;
    return String(a.orderId || a.id).localeCompare(String(b.orderId || b.id));
  }), [filtered]);

  const totalsByItem = useMemo(() => {
    const map = new Map();
    filtered.forEach((d) => {
      if (d.status === DROP_STATUS.SKIPPED || d.status === DROP_STATUS.CANCELLED) return;
      const key = `${d.brand || ''}::${d.kind || ''}::${d.itemName || 'Item'}`;
      map.set(key, (map.get(key) || 0) + Number(d.qty || 0));
    });
    return Array.from(map.entries()).map(([key, qty]) => {
      const [brand, , name] = key.split('::');
      return { key, label: brand ? `${brand} · ${name}` : name, qty };
    });
  }, [filtered]);

  const hasUnbranded = drops.some((d) => !String(d.brand || '').trim());

  return (
    <div className={styles.producerWrap}>
      <div className={styles.producerHead}>
        <div>
          <h2 className={styles.producerTitle}>Producer sheet · {date}</h2>
        </div>
        <Button
          variant="primary"
          icon={Download}
          onClick={() => onDownload(filtered)}
          disabled={downloading || filtered.length === 0}
        >
          {downloading ? 'Preparing…' : 'Download Excel'}
        </Button>
      </div>

      {(brandOptions.length > 0 || hasUnbranded) && (
        <div className={styles.brandFilterRow}>
          <span className={styles.brandFilterLabel}>Brand</span>
          <div className={styles.filterTabs}>
            <button
              type="button"
              className={`${styles.filterTab} ${brandFilter === 'ALL' ? styles.filterTabActive : ''}`}
              onClick={() => setBrandFilter('ALL')}
            >
              All brands
            </button>
            {brandOptions.map((brand) => (
              <button
                key={brand}
                type="button"
                className={`${styles.filterTab} ${brandFilter === brand ? styles.filterTabActive : ''}`}
                onClick={() => setBrandFilter(brand)}
              >
                {brand}
              </button>
            ))}
            {hasUnbranded && (
              <button
                type="button"
                className={`${styles.filterTab} ${brandFilter === '__NONE__' ? styles.filterTabActive : ''}`}
                onClick={() => setBrandFilter('__NONE__')}
              >
                No brand
              </button>
            )}
          </div>
        </div>
      )}

      {totalsByItem.length > 0 && (
        <div className={styles.producerTotals}>
          <span className={styles.producerTotalsLabel}>Totals to pack</span>
          {totalsByItem.map((t) => (
            <span key={t.key} className={styles.producerTotalChip}>
              {t.label}
              {' '}
              ×
              {t.qty}
            </span>
          ))}
        </div>
      )}

      {sorted.length === 0 ? (
        <p className={styles.producerEmpty}>No drops for this brand.</p>
      ) : (
        <div className={styles.tableScroll}>
          <table className={styles.producerTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Kind</th>
                <th>Brand</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Order ID</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Slot</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((drop, index) => (
                <tr key={drop.id}>
                  <td>{index + 1}</td>
                  <td className={styles.tdKind}>{drop.kind || '—'}</td>
                  <td>{drop.brand || '—'}</td>
                  <td>{drop.itemName || '—'}</td>
                  <td className={styles.tdQty}>{drop.qty}</td>
                  <td className={styles.tdMono}>{drop.orderId || drop.id}</td>
                  <td className={styles.tdMono}>{drop.customerPhone || '—'}</td>
                  <td className={styles.tdAddress}>{drop.address || '—'}</td>
                  <td>{drop.slotLabel || 'Morning'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function RunSheet() {
  const { user } = useAuth();
  const { notify } = useOrders();
  const { products } = useProducts();
  // Open on today's ops kanban (assign riders). Tomorrow defaults to producer packing.
  const [date, setDate] = useState(() => dateInIst(0));
  const [viewMode, setViewMode] = useState(() => VIEW_OPS);
  const [drops, setDrops] = useState([]);
  const [summary, setSummary] = useState(null);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  const tomorrow = useMemo(() => dateInIst(1), []);
  const today = useMemo(() => dateInIst(0), []);
  const isTomorrowView = date === tomorrow;
  const isTodayView = date === today;
  const showProducer = viewMode === VIEW_PRODUCER;
  const showOps = viewMode === VIEW_OPS;

  const selectDate = (nextDate) => {
    setDate(nextDate);
    setViewMode(defaultViewForDate(nextDate));
    setAssignTarget(null);
  };

  const brandByItemId = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      const brand = String(p.brand || '').trim();
      if (!brand) return;
      if (p.id) {
        map[p.id] = brand;
        map[String(p.id).toLowerCase()] = brand;
      }
      if (p.sku) {
        map[p.sku] = brand;
        map[String(p.sku).toLowerCase()] = brand;
      }
      if (p.name) map[String(p.name).trim().toLowerCase()] = brand;
    });
    return map;
  }, [products]);

  const resolveBrand = useCallback((drop) => {
    const fromApi = String(drop.brand || '').trim();
    if (fromApi) return fromApi;
    const itemId = String(drop.itemId || '').trim();
    if (itemId && brandByItemId[itemId]) return brandByItemId[itemId];
    if (itemId && brandByItemId[itemId.toLowerCase()]) return brandByItemId[itemId.toLowerCase()];
    const nameKey = String(drop.itemName || '').trim().toLowerCase();
    if (nameKey && brandByItemId[nameKey]) return brandByItemId[nameKey];
    return '';
  }, [brandByItemId]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const [{ drops: list, summary: sum }, ridersList] = await Promise.all([
        listDrops(date, user.idToken),
        listRiders(user.idToken).catch(() => []),
      ]);
      const ridersMap = Object.fromEntries(ridersList.map((r) => [r.id, r]));
      setDrops(list.map((d) => ({
        ...d,
        brand: resolveBrand(d),
        riderName: d.riderName || (d.deliveryPersonSub ? ridersMap[d.deliveryPersonSub]?.name : null),
        rider: d.deliveryPersonSub
          ? (ridersMap[d.deliveryPersonSub] || {
            id: d.deliveryPersonSub,
            name: d.riderName || d.deliveryPersonSub,
          })
          : null,
      })));
      setSummary(sum);
      setRiders(ridersList);
    } catch (err) {
      if (silent) notify(friendlyError(err));
      else setError(err.message || 'Could not load the run sheet.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, user.idToken, notify, resolveBrand]);

  useEffect(() => {
    load();
  }, [load]);

  const dropsByStatus = useMemo(() => {
    const map = {};
    RUN_SHEET_COLUMNS.forEach((col) => { map[col.key] = []; });
    drops.forEach((d) => {
      if (map[d.status]) map[d.status].push(d);
    });
    return map;
  }, [drops]);

  const withBusy = async (dropId, action, successMessage) => {
    setBusyId(dropId);
    try {
      await action();
      if (successMessage) notify(successMessage);
      await load({ silent: true });
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleAssign = async (dropIds, riderId) => {
    const dropId = dropIds[0];
    const rider = riders.find((r) => r.id === riderId);
    await withBusy(
      dropId,
      () => assignDrop(dropId, riderId, user.idToken),
      `${rider?.name || 'Rider'} assigned`,
    );
    setAssignTarget(null);
  };

  const handleDownloadExcel = (rows = drops) => {
    const list = Array.isArray(rows) ? rows : drops;
    if (!list.length) {
      notify(isTomorrowView
        ? "Tomorrow's run sheet is empty — it appears after 11:00 pm."
        : 'No drops to download for this date.');
      return;
    }
    setExporting(true);
    try {
      downloadRunSheetExcel(date, list);
      notify(`Downloaded Excel for ${date} (${list.length} drops)`);
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setExporting(false);
    }
  };

  const isEmptyLikelyTomorrow = drops.length === 0 && isTomorrowView;
  const showTomorrowReadyHint = isTomorrowView && drops.length > 0;

  return (
    <div>
      <PageHeader
        eyebrow="Subscriptions"
        title="Run sheet"
        subtitle={
          showProducer
            ? 'Producer packing list — filter by brand and download Excel for the team.'
            : 'Assign riders to morning drops. Drivers start and complete deliveries from the Delivery Partner app.'
        }
        actions={(
          <Button variant="outline" icon={RefreshCw} onClick={() => load()} disabled={loading}>
            Refresh
          </Button>
        )}
      />

      <div className={styles.runToolbar}>
        <div className={styles.runToolbarLeft}>
          <label className={styles.dateField}>
            <span>Delivery date</span>
            <input
              type="date"
              className={styles.dateInput}
              value={date}
              onChange={(e) => selectDate(e.target.value)}
            />
          </label>
        </div>
        {drops.length > 0 && (
          <div className={styles.viewToggle}>
            <div className={styles.filterTabs}>
              <button
                type="button"
                className={`${styles.filterTab} ${showOps ? styles.filterTabActive : ''}`}
                onClick={() => setViewMode(VIEW_OPS)}
              >
                Ops board
              </button>
              <button
                type="button"
                className={`${styles.filterTab} ${showProducer ? styles.filterTabActive : ''}`}
                onClick={() => setViewMode(VIEW_PRODUCER)}
              >
                Producer sheet
              </button>
            </div>
          </div>
        )}
      </div>

      {showTomorrowReadyHint && (
        <p className={styles.producerReadyHint}>
          Tomorrow&apos;s producer list is ready. Download Excel before midnight — after that this date
          becomes Today (Ops board). You can still open <strong>Producer sheet</strong> anytime.
        </p>
      )}

      {isTodayView && showOps && drops.length > 0 && (
        <p className={styles.producerReadyHint}>
          Need the packing list? Switch to <strong>Producer sheet</strong> to filter by brand and download Excel.
        </p>
      )}

      {showOps && summary && (
        <div className={boardStyles.statGrid}>
          <StatCard icon={ClipboardList} label="Pending" value={summary.pending} tint="#B8850A" />
          <StatCard icon={Truck} label="Out" value={summary.out} tint="#0D9488" />
          <StatCard icon={CheckCircle2} label="Delivered" value={summary.delivered} tint="#2E7D32" />
          <StatCard icon={XCircle} label="Failed" value={summary.failed} tint="#D32F2F" />
          {summary.stockShort > 0 && (
            <StatCard icon={PackageX} label="Stock short" value={summary.stockShort} tint="#D9724B" />
          )}
        </div>
      )}

      {loading && <p className={boardStyles.loadingText}>Loading run sheet…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load the run sheet"
          description={error}
          action={<Button variant="outline" onClick={() => load()}>Retry</Button>}
        />
      )}

      {!loading && !error && drops.length === 0 && (
        <EmptyState
          icon={ClipboardList}
          title={isEmptyLikelyTomorrow ? "Tomorrow's list isn't ready" : 'No drops for this date'}
          description={
            isEmptyLikelyTomorrow
              ? "Tomorrow's list appears after 11:00 pm (after the 10:00 pm cutoff)."
              : 'Try another date, or check that packs and customer subscriptions are active.'
          }
        />
      )}

      {!loading && !error && drops.length > 0 && showProducer && (
        <ProducerTable
          date={date}
          drops={drops}
          onDownload={handleDownloadExcel}
          downloading={exporting}
        />
      )}

      {!loading && !error && drops.length > 0 && showOps && (
        <div className={boardStyles.board}>
          {RUN_SHEET_COLUMNS.map((col) => {
            const columnDrops = dropsByStatus[col.key] || [];
            const accent = statusColors[col.key]?.fg;
            return (
              <section className={boardStyles.column} key={col.key}>
                <header className={boardStyles.columnHeader}>
                  <div className={boardStyles.columnTitleRow}>
                    <span className={boardStyles.columnDot} style={{ background: accent }} />
                    <h3 className={boardStyles.columnTitle}>{col.title}</h3>
                    <span className={boardStyles.columnCount}>{columnDrops.length}</span>
                  </div>
                  <p className={boardStyles.columnHint}>{col.hint}</p>
                </header>
                <div className={boardStyles.columnBody}>
                  {columnDrops.length === 0 ? (
                    <EmptyState title="All clear" description="No drops here right now." />
                  ) : (
                    columnDrops.map((drop) => (
                      <DropCard
                        key={drop.id}
                        drop={drop}
                        busy={busyId === drop.id}
                        onAssign={setAssignTarget}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {showOps && (
        <AssignRiderDrawer
          orders={assignTarget ? [{
            id: assignTarget.id,
            customer: { name: assignTarget.customerName || 'Customer' },
            address: assignTarget.address || '',
          }] : []}
          onClose={() => setAssignTarget(null)}
          onAssign={handleAssign}
          riders={riders}
        />
      )}
    </div>
  );
}
